
import { Component, Vue, Watch } from 'vue-property-decorator'

import { Company } from '@/types/Company'
import authModule from '@/store/auth'
import whitelabelModule from '@/store/whitelabel'
import companyModule from '@/store/company'
import MessageAlert from '@/components/MessageAlert.vue'
import UserEditor from '@/components/UserEditor.vue'
import { Whitelabel } from '@/types/Whitelabel'
import { NavItem } from '@/types/Types'

@Component({
  name: 'DefaultContainer',
  components: {
    MessageAlert,
    UserEditor
  }
})
class DefaultContainer extends Vue {
  public showNavDrawer = this.$vuetify.breakpoint.smAndUp
  public showProfileDialog = false
  public contentKey = ''
  public navItems: NavItem[] = []

  public get auth() {
    return authModule
  }

  public get whitelabel() {
    return whitelabelModule.whitelabel
  }

  public set whitelabel(wl: Whitelabel) {
    whitelabelModule.setWhitelabel(wl)
  }

  public get whitelabelLogo() {
    return (
      whitelabelModule.whitelabel.logoUrl ||
      `${whitelabelModule.whitelabel.url}/img/logo.png`
    )
  }

  public get whitelabels() {
    return whitelabelModule.whitelabels
  }

  public get company() {
    return companyModule.company!
  }

  public set company(newCompany: Company) {
    companyModule.setCompany(newCompany)
  }

  public get companies() {
    return companyModule.companies
  }

  @Watch('whitelabel')
  public refreshWhitelabelKey() {
    if (this.whitelabel._id) this.contentKey = this.whitelabel._id
  }

  @Watch('company')
  public refreshCompanyKey() {
    if (this.company._id) this.contentKey = this.company._id
  }

  public async created(): Promise<void> {
    if (authModule.isSuperadmin) {
      // if superadmin fetch last used whitelabel
      const whitelabelId = localStorage.getItem('currentWl')
      if (whitelabelId) {
        await whitelabelModule.fetch(whitelabelId)
      } else {
        whitelabelModule.setWhitelabel(authModule.user!.whitelabel)
      }
    } else {
      // set whitelabel from stored user
      whitelabelModule.setWhitelabel(authModule.user!.whitelabel)
    }
    // fetch user to prevent manipulation through cookie
    const user = await authModule.fetchUserOnce()
    if (authModule.isPartner || authModule.isLightPartner) {
      await companyModule.fetchAllPartnerCompanies()
      if (authModule.user!.company) {
        await companyModule.setCompany(authModule.user!.company)
      } else {
        await companyModule.setCompany(companyModule.companies[0] as Company)
      }
      Object.assign(user, { company: companyModule.company })
    }
    authModule.setCurrentUser(user)
    // fetch whitelabels if user is superadmin
    if (
      (!this.whitelabels || this.whitelabels.length === 0) &&
      authModule.isSuperadmin
    ) {
      whitelabelModule.fetchAll()
    }

    this.navItems.push({
      title: 'Geschäfte',
      route: 'AllVenues',
      icon: 'fas fa-store-alt'
    })

    if (!authModule.isLightPartner) {
      this.navItems.push({
        title: 'Deals',
        route: 'AllDeals',
        icon: 'fas fa-tag'
      })
    }

    // add routes to nav-drawer depending on user role
    if (authModule.isPartner || authModule.isSuperadmin) {
      this.navItems.push({
        title: 'Redemption',
        route: 'RedemptionReviews',
        icon: 'fas fa-clipboard-check'
      })
    }
    if (authModule.isAdmin || authModule.isSuperadmin) {
      this.navItems.splice(
        0,
        0,
        {
          title: 'Dashboard',
          route: 'Dashboard',
          icon: 'fas fa-tachometer-alt'
        },
        {
          title: 'Unternehmen',
          route: 'Companies',
          icon: 'fas fa-industry'
        }
      )
      this.navItems.push(
        {
          title: 'Nutzer',
          route: 'AllUsers',
          icon: 'far fa-user'
        },
        {
          title: 'Karten-Listen',
          route: 'AllCardLists',
          icon: 'far fa-images'
        },
        {
          title: 'Tags',
          route: 'AllTags',
          icon: 'fas fa-tag'
        },
        {
          title: 'Beacons',
          route: 'AllBeacons',
          icon: 'fas fa-satellite-dish'
        },
        {
          title: 'Promotion Codes',
          route: 'AllPromotionCodes',
          icon: 'fas fa-ad'
        },
        {
          title: 'Push senden',
          route: 'NewNotification',
          icon: 'fas fa-paper-plane'
        }
      )
    }
    if (
      (authModule.isPartner ||
        authModule.isAdmin ||
        authModule.isSuperadmin ||
        authModule.isLightPartner) &&
      this.whitelabel.flags.qrCodeScan
    ) {
      this.navItems.push({
        title: 'QR-Code scannen',
        route: 'ScanCode',
        icon: 'fas fa-qrcode'
      })
    }
    if (authModule.isAdmin) {
      this.navItems.splice(1, 0, {
        title: 'Plattform',
        route: 'WhitelabelDetail',
        params: { wid: this.whitelabel._id },
        icon: 'far fa-building'
      })
    }
    if (authModule.isSuperadmin) {
      this.navItems.splice(1, 0, {
        title: 'Plattformen',
        route: 'Whitelabels',
        icon: 'far fa-building'
      })
    }
  }

  public async setPartnerCompany(companyId: string): Promise<void> {
    await companyModule.fetch(companyId)
  }

  public logout(): void {
    authModule.logout()
    this.$router.replace({ name: 'Login' })
  }
}

export default DefaultContainer
